/**
 * TestCompanyBanner — fixed-top amber bar that appears when the
 * currently-signed-in admin has flipped on test-company impersonation.
 *
 * Polls `/api/admin/test-company` once per minute (and immediately on
 * mount when the user changes) so a click on "Exit" anywhere in the
 * app is reflected within seconds.
 *
 * Hidden when:
 *   - no user                      (signed-out routes)
 *   - user is not manufacturer staff (endpoint returns 403)
 *   - the feature is OFF (`impersonating: false`)
 *
 * Sits ABOVE the page header (`top-0 z-[60]`) and is sticky. Pages
 * with their own sticky header continue to work because the body is
 * pushed down by the banner's height (`h-9`).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { Warning, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const POLL_MS = 60_000;

export default function TestCompanyBanner({ user }) {
  const [state, setState] = useState({ impersonating: false, customer_label: null });
  const [busy, setBusy] = useState(false);
  const { pathname } = useLocation();

  const refresh = useCallback(async () => {
    if (!user?.email) { setState({ impersonating: false }); return; }
    try {
      const r = await axios.get(`${API}/admin/test-company`, {
        withCredentials: true,
        headers: { "X-User-Email": user.email },
      });
      setState(r.data || {});
    } catch {
      // Silent — non-allowed users get 403, that's expected. Banner
      // simply won't render.
      setState({ impersonating: false });
    }
  }, [user?.email]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [refresh, pathname]); // re-check on route change too

  const exit = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/test-company/mode`,
        { impersonating: false },
        { withCredentials: true, headers: { "X-User-Email": user.email } });
      toast.success("Exited test-company mode");
      // Force a hard refresh so all in-flight list views drop their
      // impersonation-scoped cache and re-fetch.
      window.location.reload();
    } catch {
      toast.error("Failed to exit");
      setBusy(false);
    }
  };

  if (!state?.impersonating) return null;
  return (
    <div
      className="sticky top-0 z-[60] w-full bg-amber-400 text-amber-950 border-b-2 border-amber-600 shadow-md"
      data-testid="test-company-banner"
    >
      <div className="max-w-7xl mx-auto px-4 h-9 flex items-center gap-2 text-xs">
        <Warning size={14} weight="fill" className="text-amber-800 shrink-0" />
        <span className="font-bold uppercase tracking-wider text-[10px]">Test Mode</span>
        <span className="hidden sm:inline">— viewing data as test company:</span>
        <span className="font-bold truncate">{state.customer_label || state.customer_id}</span>
        <button
          type="button"
          onClick={exit}
          disabled={busy}
          data-testid="test-company-exit-btn"
          className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-amber-950/90 text-amber-100 hover:bg-amber-950 disabled:opacity-50 transition"
        >
          <X size={10} weight="bold" /> Exit
        </button>
      </div>
    </div>
  );
}
